import { RangeSlider, Slider, Stack } from '@mantine/core'
import { IconActivityHeartbeat, IconBaselineDensityLarge, IconWaveSine } from '@tabler/icons-react'
import React, { Dispatch, SetStateAction, useEffect, useMemo } from 'react'

import { SchemaProperty } from '../../Templates/utils'
import ColorPicker from './ColorPicker'
import SelectionBoxes from './SelectionBoxes/SelectionBoxes'
import TemplateProperty from './TemplateProperty'

const roundToPrecision = (value: number, min: number, precision: number | undefined) => {
  if (!precision) {
    return value
  }
  // Ensuring the value is not less than the minimum.
  value = Math.max(value, min)

  // Calculating the rounded value.
  const roundedValue = Math.round(value / precision) * precision

  // Ensuring the result is not below the minimum.
  return Math.max(roundedValue, min)
}

// round to one decimal case a number
const round = (num: number, precision: number) => {
  const factor = 10 ** precision
  return Math.round(num * factor) / factor
}

const SchemaReader: React.FC<{
  keyName: string
  schema: SchemaProperty
  properties: any
  setProperties: Dispatch<SetStateAction<{ [key: string]: any }>>
  setDisableContinue?: Dispatch<SetStateAction<boolean>>
}> = ({ keyName, schema, properties, setProperties, setDisableContinue = () => {} }) => {
  const { title, description, type, rules, values } = schema

  // TODO: This is a hacky way to set the default value of the slider
  const defaultValueSlider = useMemo(() => {
    // Keep previously selected value
    if (properties[keyName]) {
      return properties[keyName]
    }
    // If any set default value
    if (rules) {
      return Object.keys(rules).some(key => key === 'precision')
        ? roundToPrecision(rules.min + (rules.max - rules.min) / 2, rules.min, rules.precision)
        : rules.min + (rules.max - rules.min) / 2
    }
    return 0
  }, [rules])

  useEffect(() => {
    if (type === 'number' && rules) {
      const defaultValue = Object.keys(rules).some(key => key === 'precision')
        ? roundToPrecision(rules.min + (rules.max - rules.min) / 2, rules.min, rules.precision)
        : rules.min + (rules.max - rules.min) / 2
      setProperties(prev => ({
        ...prev,
        [keyName]: defaultValue,
      }))
    }
  }, [keyName, setProperties])

  // TODO: This is a hacky way to set the default value of the range slider
  const defaultValueRangeSlider: [number, number] = useMemo(() => {
    // Keep previously selected value
    if (properties[`${keyName}Min`] && properties[`${keyName}Max`]) {
      return [properties[`${keyName}Min`], properties[`${keyName}Max`]]
    }
    // If any set default value
    if (rules && rules.lower && rules.upper && rules.precision) {
      return [rules.lower.max, rules.upper.max]
    }
    return [0, 0]
  }, [rules])

  useEffect(() => {
    if (type === 'range' && rules && rules.lower && rules.upper && rules.precision) {
      const defaultValue = [rules.lower.max, rules.upper.max]
      setProperties(prev => ({
        ...prev,
        [`${keyName}Min`]: defaultValue[0],
        [`${keyName}Max`]: defaultValue[1],
      }))
    }
  }, [keyName, setProperties])

  switch (type) {
    case 'enum':
      if (values) {
        return (
          <TemplateProperty title={title} description={description}>
            <Stack style={{ flexDirection: 'column', gap: '10px' }}>
              <SelectionBoxes
                rules={rules}
                options={values}
                // TODO: The icons should not be hardcoded, but rather be passed in from metadata
                optionIcons={[
                  <IconBaselineDensityLarge key="straight" size={35} />,
                  <IconActivityHeartbeat key="Wobble" size={35} />,
                  <IconWaveSine key="Wavy" size={35} />,
                ]}
                properties={properties}
                setProperties={setProperties}
              />
            </Stack>
          </TemplateProperty>
        )
      }
      return null
    case 'number':
      if (rules) {
        return (
          <TemplateProperty title={title} description={description}>
            <Slider
              min={rules.min}
              max={rules.max}
              defaultValue={defaultValueSlider}
              step={rules.precision}
              onChange={value => {
                setProperties(prev => ({
                  ...prev,
                  [keyName]: value,
                }))
              }}
            />
          </TemplateProperty>
        )
      }
      return null
    case 'range':
      if (rules && rules.lower && rules.upper && rules.precision) {
        return (
          <TemplateProperty title={title} description={description}>
            <RangeSlider
              minRange={rules.lower.min + rules.precision}
              min={rules.lower.min}
              max={rules.upper.max}
              label={(value: number) => round(value, rules.precision ? rules.precision * 10 : 1)}
              defaultValue={defaultValueRangeSlider}
              step={rules.precision}
              onChange={value => {
                setProperties(prev => ({
                  ...prev,
                  [`${keyName}Min`]: value[0],
                  [`${keyName}Max`]: value[1],
                }))
              }}
            />
          </TemplateProperty>
        )
      }
      return null
    case 'list':
      if (title.toLowerCase().includes('color') && rules && values) {
        return (
          <TemplateProperty title={title}>
            <ColorPicker
              min={rules.min}
              max={rules.max}
              colors={values}
              keyName={keyName}
              draftSelectedColors={properties.randomColorGuides}
              setProperties={setProperties}
              setDisableContinue={setDisableContinue}
            />
          </TemplateProperty>
        )
      }
      // if (values) {
      //   return (
      //     <TemplateProperty title={title} description={description}>
      //       <Select data={values.map(value => ({ value, label: value }))} />
      //     </TemplateProperty>
      //   )
      // }
      return null

    default:
      return null
  }
}

export default SchemaReader
